import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowLeft, Users, CheckCircle2, Clock, Coins } from "lucide-react";
import ReferralCard from "@/components/ReferralCard";
import { format } from "date-fns";

const API = "/api";

export default function ReferralPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [codeData, setCodeData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "student") {
      navigate("/student-login");
      return;
    }
    Promise.all([
      axios.get(`${API}/referrals/my-code`, { headers: getAuthHeader() }),
      axios.get(`${API}/referrals/my-history`, { headers: getAuthHeader() }),
    ])
      .then(([c, h]) => {
        setCodeData(c.data);
        setHistory(h.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/student" className="flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <h1 className="font-bold text-lg text-blue-900">LanguaConnect</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="referral-page-title">
            Refer & Earn
          </h2>
          <p className="text-slate-500 mt-2">
            Invite friends to LanguaConnect and earn <strong>+{codeData?.reward_credits || 5} paid class credits</strong> for each friend who subscribes to a package.
          </p>
        </div>

        {/* Stats */}
        {codeData?.is_paying && history && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={Users}
              label="Friends invited"
              value={history.stats.total}
              color="from-blue-500 to-blue-600"
            />
            <StatCard
              icon={CheckCircle2}
              label="Successful referrals"
              value={history.stats.completed}
              color="from-emerald-500 to-emerald-600"
            />
            <StatCard
              icon={Coins}
              label="Credits earned"
              value={history.stats.total_credits_earned}
              color="from-amber-500 to-orange-600"
            />
          </div>
        )}

        {/* Card */}
        <ReferralCard data={codeData} getAuthHeader={getAuthHeader} />

        {/* How it works */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-slate-900 mb-4">How it works</h3>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-3">
                <span className="bg-blue-100 text-blue-700 w-7 h-7 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                <span>Share your unique code or the link with friends via WhatsApp.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-100 text-blue-700 w-7 h-7 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                <span>They book a free trial or sign up using your code.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-100 text-blue-700 w-7 h-7 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                <span>When they purchase their <strong>first paid subscription</strong>, you instantly receive <strong>+{codeData?.reward_credits || 5} class credits</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-100 text-blue-700 w-7 h-7 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                <span>No cap on rewards — refer as many friends as you want!</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* History */}
        {codeData?.is_paying && (
          <Card data-testid="referral-history-card">
            <CardContent className="p-6">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> Your referral history
              </h3>
              {!history || history.items.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">
                  No referrals yet. Share your code with a friend to get started!
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-slate-500 uppercase border-b">
                      <tr>
                        <th className="py-2 pr-3">Friend</th>
                        <th className="py-2 pr-3">Source</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Reward</th>
                        <th className="py-2 pr-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.items.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100" data-testid={`referral-row-${r.id}`}>
                          <td className="py-2.5 pr-3">
                            <div className="font-medium text-slate-900">{r.referred_name || "—"}</div>
                            <div className="text-xs text-slate-500">{r.referred_email}</div>
                          </td>
                          <td className="py-2.5 pr-3 capitalize">{r.source}</td>
                          <td className="py-2.5 pr-3">
                            <StatusPill status={r.status} />
                          </td>
                          <td className="py-2.5 pr-3">
                            {r.reward_status === "granted" ? (
                              <span className="text-emerald-700 font-semibold">+{codeData?.reward_credits || 5} credits</span>
                            ) : (
                              <span className="text-slate-400 text-xs flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Pending payment
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 pr-3 text-slate-500 text-xs">
                            {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy") : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`bg-gradient-to-br ${color} p-3 rounded-xl`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
    expired: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}
